import { FC, useRef, forwardRef, useImperativeHandle, useMemo, useState, useContext } from 'react';
import V2Tabs from '@/common/components/Data/V2Tabs';
import TaskDetailApproveInfo from '@/common/components/business/ExpandStore/TaskDetailApproveInfo';
import CircleBasicInfo from '../CircleBasicInfo/index';
import Meet from 'src/common/components/business/ExpandStore/CircleTaskDetail/components/Meet';
import PointMatching from 'src/common/components/business/ExpandStore/CircleTaskDetail/components/PointMatching';
import styles from './index.module.less';
import { TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { globalAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';
import { useMethods } from '@lhb/hook';
import V2Flex from '@/common/components/Feedback/V2Flex';
import TaskTerminateInfo from '@/common/components/business/ExpandStore/TaskTerminateInfo';

enum TabKeys {
  /** 机会点 */
  changePoint = 'changePoint',
  /** 沟通记录 */
  communication = 'communication',
}

/**
 * 拓店任务：森马（参考clusterA直营）-详情，不含顶部操作按钮
 * @Description
 *
 */
const CircleTaskDetail: FC<{
  style?: any,
  /** 拓店任务详情信息 */
  detail: any;
  /** 是否审批详情内 */
  isApproval?: boolean;
  /** 审批详情 */
  aprDetail?: any;
  /** 主动刷新页面 */
  refresh?: Function;
  /** 是否需要主动隐藏操作按钮 */
  hideOperate?: boolean;
  /** 支持查看商圈详情 */
  hideClusterDetail?: boolean;
  ref?: any,
}> = forwardRef(({
  style = {},
  detail, // 拓店任务详情信息
  isApproval = false, // 是否审批详情内
  aprDetail = {}, // 审批详情
  refresh, // 主动刷新页面
  hideOperate = false, // 是否需要主动隐藏操作按钮
  hideClusterDetail = false, // 支持查看商圈详情
}, ref) => {

  // 外部调用异动审批通过操作
  useImperativeHandle(ref, () => ({
    approveTaskChange: methods.approveTaskChange,
  }));

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const [chancePointInfo, setChancePointInfo] = useState<any>({});
  const [active, setActive] = useState<TabKeys>(TabKeys.changePoint);

  const approveInfoRef = useRef<any>(null);
  const pointMatchingRef = useRef<any>(null);
  const meetRef = useRef<any>(null);
  /** 展示外部的新增机会点按钮文案，为空则不展示按钮 */
  const [addPointButtonText, setAddPointButtonText] = useState('');
  /** 展示外部的新增沟通记录按钮文案，为空则不展示按钮 */
  const [addMeetButtonText, setAddMeetButtonText] = useState('');

  // 是否只能查看不能新增数据（审批详情或者拓店任务状态非进行中时）
  // 手动控制hideOperate
  const isView = isApproval || detail.status !== TaskStatus.Processing || hideOperate;
  const chancepointAlias = useMemo(() => {
    return globalAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);
  const tabItems = useMemo(() => {
    return [
      {
        key: TabKeys.changePoint,
        label: `${chancepointAlias} ${chancePointInfo?.objectList?.length || 0}`,
      },
      {
        key: TabKeys.communication,
        label: `沟通记录`,
      },
    ];
  }, [chancePointInfo]);

  const methods = useMethods({

    /**
     * @description 审批通过
     */
    approveTaskChange: (cb: Function) => {
      approveInfoRef?.current?.approveTaskChange(cb);
    },
    /**
     * @description 新增机会点
     */
    addChancePoint: () => {
      pointMatchingRef?.current?.addChancePoint();
    },
    /**
     * @description 新增沟通记录
     */
    addCommunicationRecord: () => {
      meetRef?.current?.addCommunicationRecord();
    },

  });

  return (
    isNotEmptyAny(detail) ? <div
      className={styles.taskDetail}
      style={{ ...style }}
    >
      <TaskTerminateInfo detail={detail} />

      {/* 异动审批信息 */}
      <TaskDetailApproveInfo
        ref={approveInfoRef}
        detail={detail}
        aprDetail={aprDetail}
        isApproval={isApproval}
      />

      {/* 基本信息 */}
      <CircleBasicInfo
        taskDetail={detail}
        chancePointList={isArray(chancePointInfo?.objectList) ? chancePointInfo?.objectList : []}
        hideClusterDetail={hideClusterDetail}
      />
      <V2Tabs
        items={tabItems}
        className='mt-16'
        tabBarExtraContent={{
          right: <V2Flex gap={10}>
            {active === TabKeys.changePoint && !isView && addPointButtonText && <a onClick={methods.addChancePoint}>{addPointButtonText}</a>}
            {active === TabKeys.communication && !isView && addMeetButtonText && <a onClick={methods.addCommunicationRecord}>{addMeetButtonText}</a>}
          </V2Flex>
        }}
        onChange={(value: any) => setActive(value)}
      />

      {/* 匹配点位-机会点 */}
      {active === TabKeys.changePoint && <PointMatching
        ref={pointMatchingRef}
        id={detail.id}
        modelClusterId={detail?.modelClusterInfo?.modelClusterId}
        // modelClusterInfo={detail?.modelClusterInfo}
        refresh={refresh}
        chancepointAlias={chancepointAlias}
        chancePointInfo={chancePointInfo}
        setChancePointInfo={setChancePointInfo}
        hideOperate={hideOperate}
        isView={isView}
        showAddBtn={false}
        providerFor='clusterC'
        setAddPointButtonText={setAddPointButtonText}
      />}

      {/* 沟通记录 */}
      {active === TabKeys.communication && <Meet
        ref={meetRef}
        id={detail.id}
        isView={isView}
        showAddBtn={false}
        setAddMeetButtonText={setAddMeetButtonText}
      />}

    </div> : <></>
  );
});

export default CircleTaskDetail;
